import { Button, Stack, Card } from '@nearst/ui';
import React, { useEffect, useState } from 'react';
import { useShop } from '@services/ShopProvider';
import { createChannel, createMetaSystemUser } from '@services/channels';
import BusinessSelector from '../../BusinessSelector';
import { facebookApi, findOrCreateCatalog, findPages, loadFacebookSdk, openFBLogin } from '../../../helpers/metaCommerce';
import styles from './MetaCommerceStepOne.module.scss';

const MetaCommerceStepOne = () => {
	const { shop, updateShop } = useShop();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [loadingMessage, setLoadingMessage] = useState();

	const [businesses, setBusinesses] = useState(null);
	const [selectedBusiness, setSelectedBusiness] = useState(null);

	useEffect(loadFacebookSdk, []);

	const selectBusiness = async (id) => {
		setLoading(true);

		try {
			setLoadingMessage('Linking to your business account');
			// Create relationship
			await facebookApi(`/${import.meta.env.VITE_NEARST_BUSINESS_ID}/managed_businesses`, 'POST', {
				existing_client_business_id: id
			});

			// Create system user server-side: signing with appsecret_proof requires the FB app secret
			const userToken = window.FB.getAuthResponse()?.accessToken;
			if (!userToken) {
				throw new Error('Facebook session expired. Please log in again.');
			}
			const systemUser = await createMetaSystemUser(shop.id, id, userToken);

			setLoadingMessage('Creating product catalog');
			// Create catalog
			const catalogId = await findOrCreateCatalog(id, shop);

			// Assign catalog to system user
			await facebookApi(`/${catalogId}/assigned_users`, 'POST', {
				tasks: ['MANAGE'],
				user: systemUser.systemUserId
			});

			const facebookPages = await findPages(id);

			// Update shop record (temporary)
			await updateShop(shop.id, {
				facebookCatalogEnabled: true,
				facebookAccessToken: systemUser.accessToken,
				facebookCatalogId: catalogId,
				facebookPages,
				reprocessAt: 0 // to force full ingest next upload
			});

			// Create channel
			await createChannel(shop.id, 'meta-commerce', {
				config: {
					accessToken: systemUser.accessToken,
					catalogId: catalogId,
					businessId: id,
					systemUserId: systemUser.systemUserId,
					facebookPages
				},
				status: 'pending'
			});
		} catch (e) {
			console.log(e);
			setError(e.message || 'unexpected error');
		}
		setLoading(false);
	};

	return (
		<Card
			progress={25}
			title="Step 1: Connect your Meta Business Account"
			description="Please ensure you have access for the Meta Business Account you would like to connect."
		>
			{loading ? (
				<Card.Loader message={loadingMessage} />
			) : (
				<Stack>
					{!businesses || error ? (
						<div className={styles.buttons}>
							<Button onClick={() => openFBLogin(setError, setLoading, setBusinesses)} primary>
								Log in with existing account
							</Button>
						</div>
					) : null}

					{error && (
						<p className={styles.error}>
							Something went wrong. Please try it again after checking your account permissions.
							<br />
							<code>{error}</code>
						</p>
					)}

					{businesses && !error ? (
						<BusinessSelector
							businesses={businesses}
							setSelectedBusiness={setSelectedBusiness}
							selectBusiness={selectBusiness}
							selectedBusiness={selectedBusiness}
							setBusinesses={setBusinesses}
						/>
					) : null}
				</Stack>
			)}
		</Card>
	);
};

export default MetaCommerceStepOne;
